import { FC, useMemo } from "react";
import moment from "moment";
import { IconButton, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { CSVLink } from "react-csv";
import { ValueGetterParams } from "ag-grid-community";
import { useFlag } from "@unleash/proxy-client-react";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { useAppSelector } from "@/store/hooks";
import { resourceDetailsTargetResourceIdSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetResourceIdSelector";
import { getSavingsOpportunitiesTableColumns } from "@/components/pages/savings-opportunities/components/savingsOpportunitiesTableColumns";
import { taskAvailableStatusPropertiesSelector } from "@/store/task-management/selectors/properties/taskAvailableStatusPropertiesSelector";
import {
  KubernetesAiSavingsOpportunity,
  SavingsOpportunity,
} from "@/services/cloudchipr.api";
import { readDataByDotSeparatedString } from "@/components/pages/common/ag-grid/utils/helpers/readDataByDotSeparatedString";

interface ResourceDetailsOpportunitiesContentCsvProps {
  opportunities: SavingsOpportunity[] | KubernetesAiSavingsOpportunity[];
}

export const ResourceDetailsOpportunitiesContentCsv: FC<
  ResourceDetailsOpportunitiesContentCsvProps
> = ({ opportunities }) => {
  const resourceId = useAppSelector(resourceDetailsTargetResourceIdSelector);
  const statuses = useAppSelector(taskAvailableStatusPropertiesSelector);

  const fileName = `${formatDate(moment(), { type: "dashSeparated" })}-${resourceId}-Savings-Opportunities.csv`;
  const enableBillingCostAndForecastedEOMForOpportunities = useFlag(
    "EnableBillingCostAndForecastedEOMForOpportunities",
  );

  const csvData = useMemo(() => {
    const columns = getSavingsOpportunitiesTableColumns(
      statuses,
      enableBillingCostAndForecastedEOMForOpportunities,
    );

    return opportunities.map((opportunity) => {
      return columns.reduce(
        (acc, colDef) => {
          const key = colDef.headerName ?? colDef.field ?? colDef.colId ?? "";

          const params = {
            data: opportunity,
          } as ValueGetterParams;

          const value =
            typeof colDef.valueGetter === "function"
              ? colDef.valueGetter?.(params)
              : colDef.valueGetter;

          if (value) {
            acc[key] = value;
          } else {
            acc[key] =
              readDataByDotSeparatedString(colDef.field ?? "", opportunity) ??
              "-";
          }

          return acc;
        },
        {} as Record<string, string>,
      );
    });
  }, [
    opportunities,
    statuses,
    enableBillingCostAndForecastedEOMForOpportunities,
  ]);

  return (
    <CSVLink
      data={csvData}
      filename={fileName}
      style={{ textDecoration: "none" }}
    >
      <Tooltip arrow title="Export CSV" placement="top">
        <span>
          <IconButton size="small">
            <DownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </CSVLink>
  );
};
